// Transients: where a note or a hit starts. The clip editor draws them as
// the grey ticks a warp marker snaps to (Live's transient markers), and the
// vocoder resets its phases on them so a drum stays a hit instead of a
// smear. Spectral flux (the summed rise of each bin's log magnitude) picks
// the frame; a short amplitude envelope then puts the onset on the sample
// block where the level actually jumps, so a marker lands on the attack,
// not somewhere inside a 20 ms frame. Pure over channel arrays.

import type { PcmSource } from "@web-shared/lib/wav";
import { fftOf, hannWindow } from "@web-shared/lib/stretch/fft";

export interface Onset {
  /** File sample the attack starts at. */
  sample: number;
  /** 0..1, relative to the strongest onset in the file. */
  strength: number;
}

/** The closest two transients can sit (a flam is one hit). */
const MIN_GAP_SEC = 0.05;
/** Envelope block the attack is located to. */
const BLOCK_SEC = 0.0015;
/** How far either side the local-mean threshold looks. */
const MEAN_SEC = 0.1;
/** An onset weaker than this share of the strongest is texture, not a hit. */
const FLOOR = 0.08;

/** The analysis frame the detector uses at `sampleRate`: ~23 ms, a power of two. */
function frameFor(sampleRate: number): number {
  return Math.max(256, 2 ** Math.ceil(Math.log2(sampleRate * 0.023)));
}

/**
 * Every transient in a file, in order. Empty for silence or a file shorter
 * than one frame.
 */
export function detectOnsets(source: PcmSource): Onset[] {
  const sr = source.sampleRate;
  const len = source.length;
  const n = frameFor(sr);
  if (!(sr > 0) || len < n) return [];
  const mono = monoMix(source);
  const hop = n >> 2;
  const fft = fftOf(n);
  const win = hannWindow(n);
  const bins = (n >> 1) + 1;
  const frames = Math.ceil(len / hop) + 1;
  const flux = new Float64Array(frames);
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  let prev = new Float64Array(bins);
  let cur = new Float64Array(bins);
  for (let f = 0; f < frames; f++) {
    const start = f * hop - (n >> 1); // frame f is centred on sample f·hop
    for (let i = 0; i < n; i++) {
      const s = start + i;
      re[i] = s >= 0 && s < len ? mono[s]! * win[i]! : 0;
      im[i] = 0;
    }
    fft.forward(re, im);
    let sum = 0;
    for (let k = 0; k < bins; k++) {
      const mag = Math.log1p(100 * Math.hypot(re[k]!, im[k]!));
      cur[k] = mag;
      sum += Math.max(0, mag - prev[k]!); // frame 0 rises from silence
    }
    flux[f] = sum;
    const t = prev;
    prev = cur;
    cur = t;
  }

  let peak = 0;
  for (let f = 0; f < frames; f++) peak = Math.max(peak, flux[f]!);
  if (!(peak > 1e-9)) return [];
  const reach = Math.max(1, Math.round((MEAN_SEC * sr) / hop));
  const block = Math.max(1, Math.round(BLOCK_SEC * sr));
  const envelope = blockEnvelope(mono, block);
  const minGap = Math.round(MIN_GAP_SEC * sr);

  const out: Onset[] = [];
  let running = 0;
  for (let f = 0; f < Math.min(frames, reach); f++) running += flux[f]!;
  for (let f = 0; f < frames; f++) {
    // Sliding local mean over [f - reach, f + reach].
    const add = f + reach;
    const drop = f - reach - 1;
    if (add < frames) running += flux[add]!;
    if (drop >= 0) running -= flux[drop]!;
    const width = Math.min(frames - 1, f + reach) - Math.max(0, f - reach) + 1;
    const v = flux[f]!;
    if (v < peak * FLOOR || v < (running / width) * 1.5) continue;
    let isMax = true;
    for (let d = -3; d <= 3 && isMax; d++) {
      const g = f + d;
      if (d !== 0 && g >= 0 && g < frames && (flux[g]! > v || (flux[g]! === v && d < 0))) isMax = false;
    }
    if (!isMax) continue;
    const sample = attackIn(envelope, block, f * hop - (n >> 1), f * hop + (n >> 1), len);
    // The file stopping mid-sound splatters every bin, like an attack would;
    // a "hit" in the last half frame is that, and too late to warp to anyway.
    if (sample > len - (n >> 1)) continue;
    const last = out[out.length - 1];
    if (last && sample - last.sample < minGap) {
      if (v / peak > last.strength) out[out.length - 1] = { sample, strength: v / peak };
      continue;
    }
    out.push({ sample, strength: v / peak });
  }
  return out;
}

/** Peak amplitude of each `block`-sample block. */
function blockEnvelope(mono: Float32Array, block: number): Float32Array {
  const blocks = Math.ceil(mono.length / block);
  const env = new Float32Array(blocks);
  for (let b = 0; b < blocks; b++) {
    let m = 0;
    const end = Math.min(mono.length, (b + 1) * block);
    for (let i = b * block; i < end; i++) m = Math.max(m, Math.abs(mono[i]!));
    env[b] = m;
  }
  return env;
}

/** The block in [from, to) where the level jumps the most, as a sample. */
function attackIn(env: Float32Array, block: number, from: number, to: number, len: number): number {
  const a = Math.max(0, Math.floor(Math.max(0, from) / block));
  const b = Math.min(env.length - 1, Math.ceil(Math.min(len, to) / block));
  let best = a;
  let rise = Number.NEGATIVE_INFINITY;
  for (let i = a; i <= b; i++) {
    const r = env[i]! - (i > 0 ? env[i - 1]! : 0);
    if (r > rise) {
      rise = r;
      best = i;
    }
  }
  return best * block;
}

export function monoMix(source: PcmSource): Float32Array {
  const channels = Math.max(1, source.numberOfChannels);
  if (channels === 1) return source.getChannelData(0);
  const out = new Float32Array(source.length);
  for (let c = 0; c < channels; c++) {
    const d = source.getChannelData(c);
    for (let i = 0; i < out.length; i++) out[i] = out[i]! + d[i]! / channels;
  }
  return out;
}
